import { h, Component } from 'preact';
import produce, { Draft } from 'immer';
import { IntlProvider } from 'preact-i18n';
import debounce from 'lodash.debounce';
import {
  getKustomerCore,
  getKustomer,
  runKustomerListenersForEvent,
  getKustomerLocalStorage,
  setKustomerLocalStorage,
} from 'widget_main/globals/helpers';
import Widget from 'widget_main/components/Widget';

import {
  ChatAvailabilityContext,
  SettingsContext,
  HideNewConversationButtonSettingsContext,
  AssistantContext,
  NavigationContext,
  WidgetActionsContext,
  ConversationsContext,
  MessagesContext,
  UnreadCountsContext,
  KBContext,
  ConnectionContext,
} from 'widget_main/store';
import { Page, ConversationsState } from 'widget_main/store/types';
import {
  CONVERSATIONS_PATH,
  KB_SEARCH_PATH,
  WIDGET_TYPE_CHAT,
  WIDGET_TYPE_CHAT_KB,
  WIDGET_TYPE_KB,
  kustomerWidgetEventTypes,
  MESSAGE_THREAD_PATH,
} from 'widget_main/globals/constants';
import ChatRootIcon from 'widget_main/components/ChatRootIcon';
import { GetMessagesCallbackResponse } from 'core_main/sdk/get_messages/types';
import { SatisfactionCallbackResponse } from 'core_main/api/satisfaction/types';
import { MessageAttachmentCallbackResponse } from 'core_main/sdk/send_message/types';
import { InitAssistantCallbackResponse } from 'core_main/sdk/init_assistant/types';
import {
  getMessagesForLanguage,
  getMappedLanguage,
  initLocale,
} from 'i18n/helpers';

import {
  CreateConversationEventDetail,
  CreateConversationWidgetApiParameters,
  OpenConversationByIdEventDetail,
} from 'widget/widget_api/types';
import { ConversationMergedCallbackResponse } from 'core_main/pubnub/types';
import { OFFLINE } from 'core_main/sdk/is_chat_available/constants';
import * as helpers from './helpers';
import { AppProps, AppState } from './types';
import { INITIAL_STATE } from './constants';

const kustomerCore = getKustomerCore();

class App extends Component<AppProps, AppState> {
  constructor() {
    super();
    this.state = {
      ...INITIAL_STATE,
      assistant: {
        ...INITIAL_STATE.assistant,
        setInitAssistant: this.setInitAssistant,
      },
      navigation: {
        ...INITIAL_STATE.navigation,
        updatePage: this.updatePage,
        updateCurrentConversationId: this.updateCurrentConversationId,
      },
      widgetActions: {
        openWidget: this.openWidget,
        closeWidget: this.closeWidget,
      },
      conversations: {
        ...INITIAL_STATE.conversations,
        addConversation: this.addConversation,
        endConversation: this.endConversation,
        updateConversation: this.updateConversation,
        addOptimisticMessage: this.addOptimisticMessage,
        toggleOptimisticMessageError: this.toggleOptimisticMessageError,
        removeOptimisticMessage: this.removeOptimisticMessage,
      },
      messages: {
        ...INITIAL_STATE.messages,
        receiveMessage: this.messageReceivedHandler,
        markArticleVisited: this.markArticleVisited,
        resetHandoff: this.resetHandoff,
      },
      kb: {
        ...INITIAL_STATE.kb,
        updateCategories: this.updateCategories,
        updateArticleSearchResults: this.updateArticleSearchResults,
        updateArticleSearchFetching: this.updateArticleSearchFetching,
        updateArticles: this.updateArticles,
        updateCurrentCategoryId: this.updateCurrentCategoryId,
        updateCurrentArticleId: this.updateCurrentArticleId,
        updateArticleSearchInput: this.updateArticleSearchInput,
        articleSearchFetching: false,
      },
    };

    this.resizeHandler = debounce(this.resizeHandler, 150);
  }

  resizeHandler = () => {
    if (this.state.showWidget) {
      if (helpers.isMobileViewport()) {
        helpers.setIframeSizeFullScreen();
        this.setState(
          produce((draftState: Draft<AppState>) => {
            draftState.isMobile = true;
          }),
        );
      } else {
        helpers.setIframeSizeDefaultWidget();
        this.setState(
          produce((draftState: Draft<AppState>) => {
            draftState.isMobile = false;
          }),
        );
      }
    }
  };

  addKustomerCoreListeners = () => {
    kustomerCore.addListener(
      'onMessageReceived',
      this.agentMessageReceivedHandler,
    );

    kustomerCore.addListener(
      'onUnreadCountChange',
      this.unreadCountChangeHandler,
    );

    kustomerCore.addListener(
      'onConversationEnded',
      this.conversationEndedHandler,
    );

    kustomerCore.addListener(
      'onConversationUnended',
      this.conversationEndedHandler,
    );

    kustomerCore.addListener(
      'onSatisfactionReceived',
      this.satisfactionReceivedHandler,
    );

    kustomerCore.addListener(
      'onConversationMerged',
      this.conversationMergedHandler,
    );

    kustomerCore.addListener('onLogout', this.handleLogout);

    kustomerCore.addListener('onAssistantEnded', this.endAssistantHandler);
  };

  removeKustomerCoreListeners = () => {
    kustomerCore.removeListener(
      'onMessageReceived',
      this.agentMessageReceivedHandler,
    );

    kustomerCore.removeListener(
      'onUnreadCountChange',
      this.unreadCountChangeHandler,
    );

    kustomerCore.removeListener(
      'onConversationEnded',
      this.conversationEndedHandler,
    );

    kustomerCore.removeListener(
      'onConversationUnended',
      this.conversationEndedHandler,
    );

    kustomerCore.removeListener('onLogout', this.handleLogout);

    kustomerCore.removeListener('onAssistantEnded', this.endAssistantHandler);

    kustomerCore.removeListener(
      'onConversationMerged',
      this.conversationMergedHandler,
    );
  };

  agentMessageReceivedHandler = (response, error) => {
    const { messages } = this.state;
    const { conversationId, messageId } = response;

    const currentConversationId = messages.data[messageId]?.conversationId;
    const conversationIdChanged =
      currentConversationId && conversationId !== currentConversationId;

    if (response.sentBy.type === 'user' || conversationIdChanged) {
      this.messageReceivedHandler(response, error);
    }
  };

  messageReceivedHandler = (response, error) => {
    const { conversationId, messageId, createdAt } = response;
    if (!error) {
      this.setState((prevState) => {
        const { conversations, messages } = prevState;
        const newMessagesData = {
          ...messages.data,
          [messageId]: response,
        };
        const currentMessageIds = conversations.data[conversationId].messages;
        let newMessageIds = currentMessageIds;

        if (!currentMessageIds.includes(messageId)) {
          newMessageIds = currentMessageIds.concat(messageId);
        }

        newMessageIds = helpers.sortByMessageTimestamps(
          newMessageIds,
          newMessagesData,
        );

        return produce(prevState, (draftState: Draft<AppState>) => {
          const lastMessageId = newMessageIds[newMessageIds.length - 1];
          const lastMessage = newMessagesData[lastMessageId];
          const conversation = draftState.conversations.data[conversationId];
          conversation.preview = lastMessage?.body;
          conversation.lastMessageAt = createdAt;
          conversation.messages = newMessageIds;
          draftState.messages.data = newMessagesData;
        });
      });
    }
  };

  unreadCountChangeHandler = (response) => {
    const { total } = response;
    const { conversationId, count } = response.change;
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.unreadCounts.total = total;
        draftState.unreadCounts.conversations[conversationId] = count;
      }),
      () => {
        runKustomerListenersForEvent(
          kustomerWidgetEventTypes.onUnread,
          response,
        );
      },
    );
  };

  conversationEndedHandler = (response) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const { conversationId, ended } = response;
        const conversation = draftState.conversations.data[conversationId];
        if (conversation) {
          conversation.ended = ended;
        }
      }),
    );
  };

  endAssistantHandler = (response) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const { conversationId, isInAssistantMode } = response;
        const conversation = draftState.conversations.data[conversationId];
        if (conversation) {
          conversation.isInAssistantMode = isInAssistantMode;
        }
      }),
    );
  };

  satisfactionReceivedHandler = (response: SatisfactionCallbackResponse) => {
    const { conversationId } = response;

    if (conversationId)
      this.updateConversation(conversationId, { satisfaction: response });
  };

  conversationMergedHandler = (
    response: ConversationMergedCallbackResponse,
  ) => {
    const { sourceConversationId, targetConversationId } = response;

    this.updateConversation(sourceConversationId, {
      movedToConversationId: targetConversationId,
    });
  };

  setInitialNavigation = (settings) => {
    const { widgetType } = settings;

    let page;

    switch (widgetType) {
      case WIDGET_TYPE_KB:
        page = KB_SEARCH_PATH;
        break;
      case WIDGET_TYPE_CHAT:
        page = CONVERSATIONS_PATH;
        break;
      case WIDGET_TYPE_CHAT_KB:
        page = KB_SEARCH_PATH;
        break;
      default:
        page = CONVERSATIONS_PATH;
    }

    this.updatePage(page);
  };

  fetchHistory = (startResponse?, langModuleDefinitions?, callback?) => {
    kustomerCore.getConversations({}, (res, error) => {
      if (!res) {
        if (typeof callback === 'function') {
          callback(res, error);
        }
        return;
      }

      this.fetchMessageHistories(res.conversations);
      const initialConvoState = res.conversations
        .map((conversation) => {
          const currentConversation = this.state.conversations.data[
            conversation.conversationId
          ];

          const optimisticMessages =
            currentConversation?.optimisticMessages || [];
          const messages = currentConversation?.messages || [];
          const satisfaction = currentConversation?.satisfaction;

          return {
            ...conversation,
            messages,
            optimisticMessages,
            satisfaction,
          };
        })
        .reduce((acc, val) => {
          acc[val.conversationId] = acc[val.conversationId] || val;
          return acc;
        }, {});

      this.setState(
        produce((draftState: Draft<AppState>) => {
          draftState.unreadCounts = kustomerCore.getUnreadCount();
          draftState.conversations.data = initialConvoState;

          if (startResponse) {
            const chatAvailability = kustomerCore.isChatAvailable()
              .availability;

            draftState.isChatHidden =
              ['hidden', 'disabled'].includes(chatAvailability) ||
              !kustomerCore.isUrlAllowed({ url: window.top.location.href })
                ?.allowed;

            draftState.chatAvailability = chatAvailability;

            draftState.chatSettings = startResponse.settings;

            const startParameters = getKustomer()?.startParameters;

            draftState.hideNewConversationButtonSettings = {
              onChatHistory: !!startParameters?.hideNewConversationButtonOnHistory,
              onConversationEnd: !!startParameters?.hideNewConversationButtonOnEnd,
            };

            draftState.hideHistory = startParameters?.hideHistory;

            draftState.hideChatIcon = !!startParameters?.hideChatIcon;

            draftState.assistant.activeAssistantId =
              startParameters?.assistantId ||
              startResponse.settings?.activeAssistant;

            draftState.intlDefinitions = langModuleDefinitions;
          }
        }),
        () => {
          if (startResponse) {
            const kustomer = getKustomer();

            kustomer.startFinished = true;

            if (
              !['hidden', 'disabled'].includes(
                this.state.chatAvailability as string,
              )
            ) {
              this.checkParentLocationInterval();
            }

            this.setInitialNavigation(startResponse.settings);

            window.parent.dispatchEvent(new CustomEvent('kustomerStart'));
            this.addKustomerCoreListeners();

            this.restoreWidgetLastOpenState();

            const startCallback = kustomer?.startCallback;

            if (typeof startCallback === 'function') startCallback();
          }

          if (typeof callback === 'function') {
            callback(res);
          }
        },
      );
    });
  };

  setConnectionToOnline = () => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.connection = 'online';
      }),
    );
  };

  handleNetworkOnline = () => {
    const currentConversations = this.state.conversations.data;

    if (currentConversations && Object.keys(currentConversations).length) {
      this.fetchHistory(null, null, (_, historyError) => {
        if (historyError) return;

        this.setConnectionToOnline();
      });
    } else {
      this.setConnectionToOnline();
    }
  };

  handleNetworkOffline = () => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.connection = 'offline';
      }),
    );
  };

  handleLogin = (event) => {
    const { parameters, callback } = event.detail;

    kustomerCore.login(parameters, (res, error) => {
      if (error) {
        if (typeof callback === 'function') {
          callback(null, error);

          runKustomerListenersForEvent(
            kustomerWidgetEventTypes.onLogin,
            null,
            error,
          );
        }
        return;
      }

      this.fetchHistory(null, null, (_, historyError) => {
        if (typeof callback === 'function') {
          callback(res, historyError);
        }

        runKustomerListenersForEvent(
          kustomerWidgetEventTypes.onLogin,
          res,
          historyError,
        );
      });
    });
  };

  handleLogout = (_, error) => {
    if (error) return;

    this.setState(
      (prevState) => {
        return produce(prevState, (draftState: Draft<AppState>) => {
          draftState.conversations = {
            ...prevState.conversations,
            ...INITIAL_STATE.conversations,
          };

          draftState.messages = {
            ...prevState.messages,
            ...INITIAL_STATE.messages,
          };

          draftState.unreadCounts = INITIAL_STATE.unreadCounts;

          draftState.navigation = {
            ...prevState.navigation,
            ...INITIAL_STATE.navigation,
          };
        });
      },
      () => {
        runKustomerListenersForEvent(
          kustomerWidgetEventTypes.onLogout,
          null,
          error,
        );
      },
    );
  };

  checkParentLocationInterval() {
    const { chatSettings } = this.state;
    const { domainCriteria } = chatSettings;

    if (!domainCriteria) return;

    setInterval(() => {
      const currentLocation = window.top.location.href;
      const { parentWindowLocation, isChatHidden } = this.state;

      if (parentWindowLocation !== currentLocation) {
        const isAllowed = kustomerCore.isUrlAllowed({ url: currentLocation })
          ?.allowed;

        if (isAllowed) {
          this.setState(
            produce((draftState: Draft<AppState>) => {
              draftState.isChatHidden = false;
              draftState.currentLocation = currentLocation;
            }),
          );
        } else if (isChatHidden === false) {
          this.setState(
            produce((draftState: Draft<AppState>) => {
              draftState.isChatHidden = true;
              draftState.currentLocation = currentLocation;
            }),
          );
        }
      }
    }, 1000);
  }

  restoreWidgetLastOpenState = () => {
    if (
      getKustomerLocalStorage('wasWidgetOpen') &&
      !helpers.isMobileViewport()
    ) {
      const lastOpenedPage = getKustomerLocalStorage('lastOpenPage');

      if (lastOpenedPage) {
        this.updatePage(lastOpenedPage as Page);
      }

      if (lastOpenedPage === MESSAGE_THREAD_PATH) {
        const lastOpenConversationId = getKustomerLocalStorage(
          'lastOpenConversationId',
        );

        if (
          lastOpenConversationId &&
          this.state.conversations?.data?.[lastOpenConversationId]
        ) {
          this.updateCurrentConversationId(lastOpenConversationId as string);
        } else {
          this.updatePage(CONVERSATIONS_PATH);
        }
      }

      this.openWidget();
    }
  };

  resetHandoff = () => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const { messages } = draftState;
        const { handoff } = messages;

        if (handoff?.assistantId && !handoff?.message) {
          draftState.messages.handoff = {
            assistantId: handoff?.assistantId,
          };
        } else {
          draftState.messages.handoff = undefined;
        }

        draftState.assistant.activeAssistantId =
          getKustomer()?.startParameters?.assistantId ||
          this.state.chatSettings?.activeAssistant;
      }),
    );
  };

  handoffToMessageThread = (
    assistantId,
    message,
    initResponse?,
    initError?,
    callback?: CreateConversationWidgetApiParameters['callback'],
  ) => {
    const handoffCallback = (res, error) => {
      if (typeof callback === 'function') {
        callback(res, error);
      }

      this.state.messages.resetHandoff();
    };

    this.setState(
      produce((draftState: Draft<AppState>) => {
        if (!initError) {
          draftState.assistant.initAssistantPayload = initResponse;
          draftState.messages.handoff = {
            assistantId,
            message,
            callback: handoffCallback,
          };
          draftState.navigation.currentConversationId = undefined;
          draftState.navigation.page = MESSAGE_THREAD_PATH;
        }
      }),
      () => {
        this.openWidget();
      },
    );
  };

  createConversation = (event) => {
    const detail: CreateConversationEventDetail = event?.detail;
    const message = detail?.message;
    const assistantId = detail?.assistantId;
    const callback = detail?.callback;

    const { activeAssistantId } = this.state.assistant;
    const assistantIdToUse = assistantId || activeAssistantId;

    if (this.state.connection === OFFLINE) {
      if (typeof callback === 'function') {
        return callback(null, new Error('No Internet Connection'));
      }
      return null;
    }

    if (assistantIdToUse) {
      return kustomerCore.initAssistant(
        { assistant: assistantIdToUse, lang: getMappedLanguage() },
        (response, error) => {
          if (error && typeof callback === 'function') {
            return callback(response, error);
          }

          return this.handoffToMessageThread(
            assistantId,
            message,
            response,
            error,
            callback,
          );
        },
      );
    }

    return this.handoffToMessageThread(
      assistantId,
      message,
      null,
      null,
      callback,
    );
  };

  componentDidMount() {
    window.addEventListener('online', this.handleNetworkOnline);
    window.addEventListener('offline', this.handleNetworkOffline);
    window.addEventListener('kustomerOpen', this.openWidget);
    window.addEventListener('kustomerClose', this.closeWidget);
    window.addEventListener(
      'kustomerCreateConversation',
      this.createConversation,
    );
    window.addEventListener('kustomerLogin', this.handleLogin);
    window.addEventListener(
      'kustomerOpenConversationById',
      this.openConversationById,
    );
    window.addEventListener(
      'kustomerGetOpenConversations',
      this.getOpenConversations,
    );
    window.parent.addEventListener('resize', this.resizeHandler);

    if (kustomerCore) {
      kustomerCore.init(
        {
          scheduleId: getKustomer()?.startParameters?.scheduleId,
          brandId: getKustomer()?.startParameters?.brandId,
          lang: getKustomer()?.startParameters?.lang,
        },
        (response, error) => {
          if (error) return;
          helpers
            .setThemeVariables(response.settings)
            .then(initLocale)
            .then(getMessagesForLanguage)
            .then((langModule) => {
              this.fetchHistory(response, langModule?.default);
            })
            .catch((error) => {
              this.fetchHistory(response);
              console.error(`Error initializing Kustomer: ${error}`);
            });
        },
      );
    }
  }

  componentDidUpdate(_, prevState) {
    const { isChatHidden, hideChatIcon, showWidget } = this.state;
    if (!prevState.isChatHidden && isChatHidden) {
      helpers.setIframeSizeHidden();
    }

    if (prevState.isChatHidden && !isChatHidden) {
      if (showWidget) {
        this.resizeHandler();
      } else {
        helpers.setIframeSizeChatIcon(hideChatIcon);
      }
    }
  }

  componentWillUnmount() {
    this.removeKustomerCoreListeners();
    window.removeEventListener('online', this.handleNetworkOnline);
    window.removeEventListener('offline', this.handleNetworkOffline);
    window.removeEventListener('kustomerOpen', this.openWidget);
    window.removeEventListener('kustomerClose', this.closeWidget);
    window.removeEventListener(
      'kustomerCreateConversation',
      this.createConversation,
    );
    window.removeEventListener('kustomerLogin', this.handleLogin);
    window.removeEventListener(
      'kustomerOpenConversationById',
      this.openConversationById,
    );
    window.removeEventListener(
      'kustomerGetOpenConversations',
      this.getOpenConversations,
    );
    window.parent.removeEventListener('resize', this.resizeHandler);
  }

  fetchMessageHistories(conversations) {
    conversations.forEach((conversation) => {
      const { conversationId } = conversation;
      kustomerCore.getMessages(
        {
          conversationId,
        },
        (response: GetMessagesCallbackResponse, error) => {
          if (error) {
            return;
          }
          const messagesById = response.messages.reduce((acc, val) => {
            acc[val.messageId] = acc[val.messageId] || val;
            return acc;
          }, {});

          this.setState(
            produce((draftState: Draft<AppState>) => {
              const draftConversation =
                draftState.conversations.data[conversationId];

              const messageIds = response.messages.map(
                (message) => message.messageId,
              );

              draftConversation.messages = helpers.sortByMessageTimestamps(
                messageIds,
                messagesById,
              );

              const lastMessageId =
                draftConversation.messages[messageIds.length - 1];
              const lastMessage = messagesById[lastMessageId];

              draftConversation.preview = lastMessage?.body;
              draftConversation.satisfaction = response.satisfaction;

              Object.assign(draftState.messages.data, messagesById);
            }),
          );
        },
      );
    });
  }

  setInitAssistant = (initAssistant?: InitAssistantCallbackResponse) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.assistant.initAssistantPayload = initAssistant;
      }),
    );
  };

  openWidget = (event?: CustomEvent | MouseEvent | KeyboardEvent) => {
    const { isChatHidden } = this.state;
    if (isChatHidden) {
      return;
    }

    const isMobile = helpers.isMobileViewport();

    this.setState(
      (prevState) =>
        produce(prevState, (draftState: Draft<AppState>) => {
          draftState.showWidget = true;
          draftState.isMobile = isMobile;
        }),
      () => {
        if (isMobile) {
          helpers.setIframeSizeFullScreen();
        } else {
          helpers.setIframeSizeDefaultWidget();
        }

        setKustomerLocalStorage('wasWidgetOpen', true);

        if (event?.detail?.callback) {
          event.detail.callback();
        }

        kustomerCore.sendPresenceActivity({ presence: 'online' });

        runKustomerListenersForEvent(kustomerWidgetEventTypes.onOpen);
      },
    );
  };

  closeWidget = (event?: CustomEvent | MouseEvent) => {
    const { isChatHidden, hideChatIcon } = this.state;

    if (isChatHidden) {
      return;
    }

    helpers.setIframeSizeChatIcon(hideChatIcon);
    this.setState(
      (prevState) =>
        produce(prevState, (draftState: Draft<AppState>) => {
          draftState.showWidget = false;
        }),
      () => {
        setKustomerLocalStorage('wasWidgetOpen', false);
        if (event?.detail?.callback) {
          event.detail.callback();
        }

        kustomerCore.sendPresenceActivity({ presence: 'offline' });

        runKustomerListenersForEvent(kustomerWidgetEventTypes.onClose);
      },
    );
  };

  openConversationById = (event: CustomEvent) => {
    const { conversations } = this.state;

    const detail: OpenConversationByIdEventDetail = event?.detail;

    const conversationId = detail?.conversationId;
    const callback = detail?.callback;

    if (!conversationId || !conversations.data[conversationId]) {
      if (callback) {
        return callback(null, new Error('Invalid conversationId provided.'));
      }
    } else {
      return this.setState(
        (prevState) =>
          produce(prevState, (draftState: Draft<AppState>) => {
            draftState.navigation.currentConversationId = conversationId;
            draftState.navigation.page = MESSAGE_THREAD_PATH;
          }),
        () => {
          this.openWidget();
          if (callback) callback(null);
        },
      );
    }

    return undefined;
  };

  getOpenConversations = (event: CustomEvent) => {
    const { conversations } = this.state;

    const openConversations = Object.values(conversations.data)
      .filter((conversation) => !conversation.ended)
      .map((conversation) => ({
        conversationId: conversation.conversationId,
      }));

    if (event?.detail?.callback) {
      event.detail.callback(openConversations);
    }
  };

  updatePage = (page: Page) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.navigation.page = page;
      }),
    );
  };

  updateCurrentConversationId = (conversationId?: string) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.navigation.currentConversationId = conversationId;
      }),
    );
  };

  addConversation = (conversation, initialMessages) => {
    const { conversationId, createdAt, isInAssistantMode } = conversation;
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.conversations.data[conversationId] = {
          conversationId,
          messages: [],
          optimisticMessages: [],
          createdAt,
          isInAssistantMode,
          initialMessages,
        };
      }),
    );
  };

  updateConversation: ConversationsState['updateConversation'] = (
    conversationId,
    conversationAttributes,
  ) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const current = draftState.conversations.data[conversationId];

        draftState.conversations.data[conversationId] = {
          ...current,
          ...conversationAttributes,
        };
      }),
    );
  };

  updateCategories = (categories, rootCategory) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.categories = categories;
        draftState.kb.rootCategory = rootCategory;
      }),
    );
  };

  updateArticles = (articles) => {
    const articlesToAdd = articles.reduce((acc, val) => {
      acc[val.articleId] = val;

      return acc;
    }, {});

    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.articles = {
          ...this.state.kb.articles,
          ...articlesToAdd,
        };
      }),
    );
  };

  updateArticleSearchResults = (articles) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.articleSearchResults = articles;
      }),
    );
  };

  updateArticleSearchInput = (term: string) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.articleSearchInput = term;
      }),
    );
  };

  updateCurrentCategoryId = (categoryId) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.currentCategoryId = categoryId;
      }),
    );
  };

  updateCurrentArticleId = (articleId) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.currentArticleId = articleId;
      }),
    );
  };

  updateArticleSearchFetching = (fetching: boolean) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.kb.articleSearchFetching = fetching;
      }),
    );
  };

  endConversation = (conversationId) => {
    kustomerCore.endConversation({
      conversationId,
    });
  };

  addOptimisticMessage = (conversationId, message) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        draftState.conversations.data[conversationId].optimisticMessages.push(
          message,
        );
      }),
    );
  };

  toggleOptimisticMessageError = (conversationId, optimisticId, value) => {
    this.setState((prevState) => {
      const { optimisticMessages } = prevState.conversations.data[
        conversationId
      ];
      return produce(prevState, (draftState: Draft<AppState>) => {
        draftState.conversations.data[
          conversationId
        ].optimisticMessages = optimisticMessages.map((message) => {
          if (message.messageId === optimisticId) {
            return {
              ...message,
              showError: value,
            };
          }
          return message;
        });
      });
    });
  };

  removeOptimisticMessage = (conversationId, optimisticMessageId) => {
    this.setState(
      produce((draftState: Draft<AppState>) => {
        const conversation = draftState.conversations.data[conversationId];

        let attachmentsToRevoke = [] as MessageAttachmentCallbackResponse[];

        const withoutOptimisticMessage = conversation.optimisticMessages.filter(
          (message) => {
            const isMessage = message.messageId === optimisticMessageId;

            if (isMessage && message.attachments) {
              attachmentsToRevoke = message.attachments;
            }

            return !isMessage;
          },
        );

        attachmentsToRevoke.forEach((attachment) => {
          if (attachment.url) URL.revokeObjectURL(attachment.url);
        });

        conversation.optimisticMessages = withoutOptimisticMessage;
      }),
    );
  };

  markArticleVisited = (messageId, articleId) => {
    this.setState((prevState) => {
      return produce(prevState, (draftState: Draft<AppState>) => {
        const message = draftState.messages.data[messageId];

        const article = message?.meta?.articles?.find(
          (article) => article.articleId === articleId,
        );

        if (article) {
          article.visited = 1;
        }
      });
    });
  };

  renderChatRootIcon() {
    const {
      hideChatIcon,
      chatSettings,
      unreadCounts,
      intlDefinitions,
    } = this.state;

    if (hideChatIcon) {
      return null;
    }

    return (
      <IntlProvider definition={intlDefinitions}>
        <UnreadCountsContext.Provider value={unreadCounts}>
          <SettingsContext.Provider value={chatSettings}>
            <ChatRootIcon onClick={this.openWidget} />
          </SettingsContext.Provider>
        </UnreadCountsContext.Provider>
      </IntlProvider>
    );
  }

  renderWidget() {
    const {
      navigation,
      chatAvailability,
      chatSettings,
      hideNewConversationButtonSettings,
      assistant,
      widgetActions,
      conversations,
      messages,
      unreadCounts,
      kb,
      intlDefinitions,
      connection,
    } = this.state;
    return (
      <IntlProvider definition={intlDefinitions}>
        <NavigationContext.Provider value={navigation}>
          <ChatAvailabilityContext.Provider value={chatAvailability}>
            <HideNewConversationButtonSettingsContext.Provider
              value={hideNewConversationButtonSettings}
            >
              <SettingsContext.Provider value={chatSettings}>
                <AssistantContext.Provider value={assistant}>
                  <WidgetActionsContext.Provider value={widgetActions}>
                    <ConversationsContext.Provider value={conversations}>
                      <UnreadCountsContext.Provider value={unreadCounts}>
                        <MessagesContext.Provider value={messages}>
                          <KBContext.Provider value={kb}>
                            <ConnectionContext.Provider value={connection}>
                              <Widget
                                isMobile={this.state.isMobile}
                                hideHistory={this.state.hideHistory}
                              />
                            </ConnectionContext.Provider>
                          </KBContext.Provider>
                        </MessagesContext.Provider>
                      </UnreadCountsContext.Provider>
                    </ConversationsContext.Provider>
                  </WidgetActionsContext.Provider>
                </AssistantContext.Provider>
              </SettingsContext.Provider>
            </HideNewConversationButtonSettingsContext.Provider>
          </ChatAvailabilityContext.Provider>
        </NavigationContext.Provider>
      </IntlProvider>
    );
  }

  render(_, state) {
    const { isChatHidden, showWidget } = state;
    if (isChatHidden) {
      return null;
    }

    return showWidget ? this.renderWidget() : this.renderChatRootIcon();
  }
}

export default App;
